import * as R from "ramda";
import { currencyFormatter, capitalizeWords, numberFormatter } from "../MMM/MMMUtils";
import { getTotalsByDate } from "../MMM/ModelResults/DataFormatters";
import { CompanyFiscalCal } from "@blisspointmedia/bpm-types/dist/FiscalCalendar";
import { formatMoney, formatNumber } from "../utils/format-utils";
import { ResponseCurves } from "@blisspointmedia/bpm-types/dist/GeoMMM";

export const OUTCOME_TYPE_STRING = {
  kpiVolume: "KPI Volume",
  revenue: "Revenue",
};

export const OUTCOME_FORMATTER_TYPE = {
  kpiVolume: numberFormatter,
  revenue: currencyFormatter,
};

/*
 * INVESTMENT PERFORMANCE
 */

export const snapShotDataSort = (data: any[], snapChartSortValue: string): any[] => {
  let snapData;

  switch (snapChartSortValue) {
    case "Alphabetical":
      snapData = R.sortBy(R.prop("name"), data);
      break;
    case "Highest to lowest":
      snapData = R.sortBy(R.compose(R.negate, R.prop("value")), data);
      break;
    case "Lowest to highest":
      snapData = R.sortBy(R.prop("value"), data);
      break;
    default:
      snapData = data;
  }

  return snapData;
};

export const formattedSnapShotData = ({
  data,
  groupBy,
  conversionTypeString,
  type,
  outcomeVariable,
}: {
  data: any[];
  groupBy: string;
  conversionTypeString: string;
  type: string;
  outcomeVariable: string;
}): { barData; titleData } => {
  let groupedData;
  switch (groupBy) {
    case "platform": {
      // Group the data by channel and then by platform
      const chanPlatGrouped = R.pipe(
        //@ts-ignore
        R.groupBy(R.prop("channel")),
        //@ts-ignore
        R.map(R.groupBy(R.prop("platform")))
        //@ts-ignore
      )(data);

      // format grouped data to have key as [channel, platform]: data instead of channel: {platform: data}
      let formattedChanPlat = {} as Record<string, any>;
      Object.keys(chanPlatGrouped as Record<string, any>).forEach((key: string) => {
        Object.keys((chanPlatGrouped as Record<string, any>)[key]).forEach((platform: string) => {
          formattedChanPlat[`${key}${platform ? `, ${platform}` : ""}`] = [
            (chanPlatGrouped as Record<string, any>)[key][platform],
          ];
        });
      });

      Object.keys(formattedChanPlat).forEach(key => {
        formattedChanPlat[key] = formattedChanPlat[key][0];
      });

      groupedData = formattedChanPlat;
      break;
    }
    default:
      groupedData = R.pipe(
        //@ts-ignore
        R.groupBy(R.prop(groupBy))
        //@ts-ignore
      )(data);
  }

  // Sum of incrementalOutcome values by grouping
  let sumOfOutcome: { [key: string]: { name: string; value: number } } = {};
  Object.keys(groupedData as any[]).forEach((item: any) => {
    // Sum of incrementalOutcome values
    const incrementalOutcomeSum = R.pipe(
      //@ts-ignore
      R.pluck("incrementalOutcome"),
      R.sum
      //@ts-ignore
    )(groupedData[item]);

    sumOfOutcome[item] = {
      name: capitalizeWords(item),
      value: incrementalOutcomeSum,
    };
  });

  // Sum of incrementalOutcome lower bounds by grouping
  let sumOfOutcomeLower: { [key: string]: { name: string; value: number } } = {};
  Object.keys(groupedData as any[]).forEach((item: any) => {
    const incrementalOutcomeLowerSum = R.pipe(
      // @ts-ignore
      R.pluck("incrementalOutcomeLower"),
      R.sum
      // @ts-ignore
    )(groupedData[item]);
    sumOfOutcomeLower[item] = {
      name: capitalizeWords(item),
      value: incrementalOutcomeLowerSum,
    };
  });

  // Sum of incrementalOutcome upper bounds by grouping
  let sumOfOutcomeUpper: { [key: string]: { name: string; value: number } } = {};
  Object.keys(groupedData as any[]).forEach((item: any) => {
    const incrementalOutcomeUpperSum = R.pipe(
      // @ts-ignore
      R.pluck("incrementalOutcomeUpper"),
      R.sum
      // @ts-ignore
    )(groupedData[item]);
    sumOfOutcomeUpper[item] = {
      name: capitalizeWords(item),
      value: incrementalOutcomeUpperSum,
    };
  });

  // Sum of spend values by grouping
  let sumOfSpend: { [key: string]: { name: string; value: number } } = {};
  Object.keys(groupedData as any[]).forEach((item: any) => {
    // Sum of incrementalOutcome values
    const spendSum = R.pipe(
      //@ts-ignore
      R.pluck("spend"),
      R.sum
      //@ts-ignore
    )(groupedData[item]);

    sumOfSpend[item] = {
      name: capitalizeWords(item),
      value: spendSum,
    };
  });

  // @ts-ignore
  const sumOfOutcomeAll = R.pipe(R.pluck("value"), R.sum)(Object.values(sumOfOutcome));
  // @ts-ignore
  const sumOfSpendAll = R.pipe(R.pluck("value"), R.sum)(Object.values(sumOfSpend));
  // TODO: Update later to account for covariates
  // const sumOfOutcomeAllSansCovariates =
  //   sumOfOutcomeAll - (sumOfOutcome.find(sum => sum.name === "Baseline")?.value ?? 0);
  const overallCPA = sumOfSpendAll / sumOfOutcomeAll;
  const overallROAS = sumOfOutcomeAll / sumOfSpendAll;

  const calcCpaRoasSpend = Object.keys(groupedData as any[]).map((key: any) => {
    const spendSum = sumOfSpend[key].value;

    // Sum of incremental outcome values
    const incrementalOutcomeSum = sumOfOutcome[key].value;
    const incrementalOutcomeSumLower = sumOfOutcomeLower[key].value;
    const incrementalOutcomeSumUpper = sumOfOutcomeUpper[key].value;

    const { spendOverOutcome, spendOverOutcomeLower, spendOverOutcomeUpper } = (() => {
      const calcSpendOverOutcome = spendSum / incrementalOutcomeSum;
      const calcSpendOverOutcomeLower = spendSum / incrementalOutcomeSumLower;
      const calcSpendOverOutcomeUpper = spendSum / incrementalOutcomeSumUpper;

      return {
        spendOverOutcome:
          calcSpendOverOutcome === Infinity || isNaN(calcSpendOverOutcome)
            ? null
            : calcSpendOverOutcome,
        spendOverOutcomeLower:
          calcSpendOverOutcomeLower === Infinity || isNaN(calcSpendOverOutcomeLower)
            ? null
            : calcSpendOverOutcomeLower,
        spendOverOutcomeUpper:
          calcSpendOverOutcomeUpper === Infinity || isNaN(calcSpendOverOutcomeUpper)
            ? null
            : calcSpendOverOutcomeUpper,
      };
    })();

    const { outcomeOverSpend, outcomeOverSpendLower, outcomeOverSpendUpper } = (() => {
      const calcOutcomeOverSpend = incrementalOutcomeSum / spendSum;
      const calcOutcomeOverSpendLower = incrementalOutcomeSumLower / spendSum;
      const calcOutcomeOverSpendUpper = incrementalOutcomeSumUpper / spendSum;

      return {
        outcomeOverSpend:
          calcOutcomeOverSpend === Infinity || isNaN(calcOutcomeOverSpend)
            ? null
            : calcOutcomeOverSpend,
        outcomeOverSpendLower:
          calcOutcomeOverSpendLower === Infinity || isNaN(calcOutcomeOverSpendLower)
            ? null
            : calcOutcomeOverSpendLower,
        outcomeOverSpendUpper:
          calcOutcomeOverSpendUpper === Infinity || isNaN(calcOutcomeOverSpendUpper)
            ? null
            : calcOutcomeOverSpendUpper,
      };
    })();

    const spendAndEffectShare = {
      name: capitalizeWords(key),
      value: (spendSum / sumOfSpendAll) * 100,
      value2: (incrementalOutcomeSum / sumOfOutcomeAll) * 100,
      label: `Spend ${currencyFormatter.format(spendSum)}`,
      label2: {
        top: conversionTypeString,
        bottom:
          outcomeVariable === "revenue"
            ? currencyFormatter.format(incrementalOutcomeSum)
            : `${numberFormatter.format(incrementalOutcomeSum)}`,
      },
      spendOverOutcome,
      spend_volume: spendSum,
      effect_volume: incrementalOutcomeSum,
    };

    if (type === "spendAndEffectShare") {
      return spendAndEffectShare;
    }

    const val = type === "roas" ? outcomeOverSpend : spendOverOutcome;
    const valLower = type === "roas" ? outcomeOverSpendLower : spendOverOutcomeUpper;
    const valUpper = type === "roas" ? outcomeOverSpendUpper : spendOverOutcomeLower;

    return {
      name: capitalizeWords(key),
      value: val,
      valueLower: valLower,
      valueUpper: valUpper,
      errorX:
        key !== "baseline" && val != null && valLower != null && valUpper != null
          ? [val - valLower, valUpper - val]
          : undefined,
      label:
        type === "roas"
          ? `${((outcomeOverSpend ?? 0) / overallROAS).toFixed(2)} x overall`
          : `${((spendOverOutcome ?? 0) / overallCPA).toFixed(2)} x overall`,
      labelRawNumber:
        type === "roas"
          ? (outcomeOverSpend ?? 0) / overallROAS
          : (spendOverOutcome ?? 0) / overallCPA,
    };
  });

  const responseBarData = Object.keys(groupedData as any[]).map((key: any) => {
    const val = sumOfOutcome[key].value;
    const lower = sumOfOutcomeLower[key].value;
    const upper = sumOfOutcomeUpper[key].value;

    const responseBarValue = (val / sumOfOutcomeAll) * 100;
    const lowerBarValue = (lower / sumOfOutcomeAll) * 100;
    const upperBarValue = (upper / sumOfOutcomeAll) * 100;

    return {
      name: sumOfOutcome[key].name,
      value: responseBarValue,
      valueLower: lowerBarValue,
      valueUpper: upperBarValue,
      errorX:
        key !== "baseline" &&
        responseBarValue != null &&
        lowerBarValue != null &&
        upperBarValue != null
          ? [responseBarValue - lowerBarValue, upperBarValue - responseBarValue]
          : undefined,
      label:
        outcomeVariable === "revenue"
          ? currencyFormatter.format(val)
          : `${numberFormatter.format(val)}`,
      labelRawNumber: val,
    };
  });

  switch (type) {
    case "revenue":
      return { barData: responseBarData, titleData: currencyFormatter.format(sumOfOutcomeAll) };
    case "kpiVolume":
      return { barData: responseBarData, titleData: numberFormatter.format(sumOfOutcomeAll) };
    case "costPerAcquisition":
      return {
        barData: calcCpaRoasSpend,
        titleData: currencyFormatter.format(overallCPA),
      };
    case "roas":
      return { barData: calcCpaRoasSpend, titleData: overallROAS.toFixed(2) };
    default:
      return {
        barData: calcCpaRoasSpend,
        titleData: null,
      };
  }
};

const getNewD = ({
  totalsByDate,
  relative,
}: {
  totalsByDate: Record<string, Record<string, number>>;
  relative: boolean;
}) => {
  let newD = [] as any[];
  Object.keys(totalsByDate).forEach((date, index) => {
    // if relative view for chart; divide each value by the total sum of the date
    if (relative) {
      const keys = R.keys(totalsByDate[date]);
      let totalSum = 0;
      // Exclude the lower, upper, and band values form total sum
      keys.forEach(key => {
        if (!key.includes("_$")) {
          totalSum += totalsByDate[date][key];
        }
      });
      // const totalSum = R.sum(R.values(totalsByDate[key]));
      R.keys(totalsByDate[date]).forEach(item => {
        totalsByDate[date][item] = R.divide(totalsByDate[date][item], totalSum) * 100;
      });
    }
    newD[index] = {};
    newD[index].date = date;

    newD[index] = { ...newD[index], ...totalsByDate[date] };
  });
  return newD;
};

// Change cptb data into a format that can be used by the chart
// {data: string, name of type: spend}
// also returns areas list from name attribute
// when y axis needs to be calculated from two data points, data1Key/data2Key is used
export const formattedAreaData = ({
  data,
  data1Key,
  groupBy,
  dateGrouping,
  data2Key,
  fiscalCal,
  relative = false,
}: {
  data: any[];
  data1Key: string;
  groupBy: string;
  dateGrouping: string;
  fiscalCal: CompanyFiscalCal;
  data2Key?: string;
  relative?: boolean;
}): { newD: any[]; areas: any; groupedAreas: any } => {
  let groupedData;
  let grouping;
  let channels;
  let newD = [] as any[];
  let totalsByDate: Record<string, Record<string, number>> = {};

  switch (groupBy) {
    case "channel": {
      const groupByChannel = R.groupBy(item => R.prop(groupBy, item), data);
      channels = Object.keys(groupByChannel);
      groupedData = Object.keys(groupByChannel).map(key => {
        return R.groupBy(item => R.prop("date", item), groupByChannel[key]);
      });
      let channelsData = {} as any;
      groupedData.forEach((item, index) => {
        channelsData[channels[index]] = [] as any[];
        Object.keys(item).forEach(key => {
          const [dKeyTotal, dKeyLower, dKeyUpper] = item[key].reduce(
            ([acc, lower, upper], obj) => {
              // treat spend < 10 as 0
              let estSum, lowerSum, upperSum;

              if (data1Key === "spend" && obj[data1Key] < 10) {
                estSum = acc;
              } else {
                estSum = acc + obj[data1Key];
              }
              // Spend doesn't have lower and upper bounds
              if (data1Key !== "spend") {
                lowerSum = lower + obj[`${data1Key}Lower`];
                upperSum = upper + obj[`${data1Key}Upper`];
              } else {
                lowerSum = estSum;
                upperSum = estSum;
              }

              return [estSum, lowerSum, upperSum];
            },
            [0, 0, 0]
          );

          if (data2Key) {
            const [d2KeyTotal, d2KeyLower, d2KeyUpper] = item[key].reduce(
              ([acc, lower, upper], obj) => {
                let estSum, lowerSum, upperSum;

                if (data2Key === "spend" && obj[data2Key] < 10) {
                  estSum = acc;
                } else {
                  estSum = acc + obj[data2Key];
                }
                // Spend doesn't have lower and upper bounds
                if (data2Key !== "spend") {
                  lowerSum = lower + obj[`${data2Key}Lower`];
                  upperSum = upper + obj[`${data2Key}Upper`];
                } else {
                  lowerSum = estSum;
                  upperSum = estSum;
                }

                return [estSum, lowerSum, upperSum];
              },
              [0, 0, 0]
            );

            let calcVal, calcValLower, calcValUpper;
            // calculating ROAS or CPA
            if (d2KeyTotal === 0) {
              calcVal = null;
              calcValLower = null;
              calcValUpper = null;
            } else {
              calcVal = dKeyTotal / d2KeyTotal;

              if (data1Key !== "spend") {
                // ROAS calculation: outcome/spend
                calcValLower = dKeyLower / d2KeyTotal;
                calcValUpper = dKeyUpper / d2KeyTotal;
              } else {
                // CPA calculation: spend/outcome
                calcValLower = d2KeyUpper === 0 ? null : dKeyTotal / d2KeyUpper;
                calcValUpper = d2KeyLower === 0 ? null : dKeyTotal / d2KeyLower;
              }
            }

            channelsData[channels[index]].push({
              date: key,
              [channels[index]]: calcVal === Infinity || isNaN(calcVal) ? null : calcVal,
              [`${channels[index]}_$lower`]:
                calcValLower === Infinity || isNaN(calcValLower) ? null : calcValLower,
              [`${channels[index]}_$upper`]:
                calcValUpper === Infinity || isNaN(calcValUpper) ? null : calcValUpper,
              [`${channels[index]}_$band`]: isNaN(calcValUpper - calcValLower)
                ? null
                : calcValUpper - calcValLower,
            });
          } else {
            channelsData[channels[index]].push({
              date: key,
              [channels[index]]: dKeyTotal,
              [`${channels[index]}_$lower`]: dKeyLower,
              [`${channels[index]}_$upper`]: dKeyUpper,
              [`${channels[index]}_$band`]: isNaN(dKeyUpper - dKeyLower)
                ? null
                : dKeyUpper - dKeyLower,
            });
          }
        });
      });

      const e = Object.keys(channelsData).map(key => {
        return channelsData[key];
      });

      const groupedByDate = R.groupBy(item => R.prop("date", item), e.flat());
      totalsByDate = getTotalsByDate(dateGrouping, groupedByDate, fiscalCal);

      newD = getNewD({ totalsByDate, relative });

      break;
    }
    case "platform": {
      // group by channel and then platform
      const d = R.groupBy(item => R.prop("channel", item), data);
      let chanPlat = {} as any;
      Object.keys(d).forEach(key => {
        chanPlat[key] = R.groupBy(item => R.prop("platform", item), d[key]);
      });

      interface TransformedChanPlatData {
        data: any[];
        grouping: {
          channel: string;
          platform: string | null;
        };
      }
      const transformChanPlat = (
        originalData: Record<string, Record<string, Record<string, Record<string, any[]>>>>
      ): Record<string, TransformedChanPlatData> => {
        const transformedData: Record<string, TransformedChanPlatData> = {};

        Object.keys(originalData).forEach(channel => {
          Object.keys(originalData[channel]).forEach(platform => {
            const key = `${channel},${platform}`;
            const data = originalData[channel][platform];
            transformedData[key] = {
              data: [data],
              grouping: {
                channel: capitalizeWords(channel),
                platform:
                  platform === "null" && Object.values(originalData[channel]).length === 1
                    ? "Total"
                    : platform === "null"
                    ? `All ${capitalizeWords(channel)}`
                    : capitalizeWords(platform),
              },
            };
          });
        });

        return transformedData;
      };

      const transformedChanPlat = transformChanPlat(chanPlat);

      // format grouped data to have key as [channel, platform]: data instead of channel: {platform: data}
      let formattedChanPlat = {} as any;
      Object.keys(chanPlat).forEach(key => {
        Object.keys(chanPlat[key]).forEach(platform => {
          formattedChanPlat[`${key},${platform}`] = chanPlat[key][platform];
        });
      });

      channels = Object.keys(formattedChanPlat);
      groupedData = Object.keys(formattedChanPlat).map(key => {
        //@ts-ignore
        return R.groupBy(item => R.prop("date", item), formattedChanPlat[key]);
      });
      let nameData = {} as any;
      // sums data1Key values from each object in the array
      // take date grouped data and and turn in into groupedName: {date, groupedName: data}

      groupedData.forEach((item, index) => {
        nameData[channels[index]] = [] as any[];
        Object.keys(item).forEach(key => {
          const [dKeyTotal, dKeyLower, dKeyUpper] = item[key].reduce(
            ([acc, lower, upper], obj) => {
              // treat spend < 10 as 0
              let estSum, lowerSum, upperSum;

              if (data1Key === "spend" && obj[data1Key] < 10) {
                estSum = acc;
              } else {
                estSum = acc + obj[data1Key];
              }
              // Spend doesn't have lower and upper bounds
              if (data1Key !== "spend") {
                lowerSum = lower + obj[`${data1Key}Lower`];
                upperSum = upper + obj[`${data1Key}Upper`];
              } else {
                lowerSum = estSum;
                upperSum = estSum;
              }

              return [estSum, lowerSum, upperSum];
            },
            [0, 0, 0]
          );
          if (data2Key) {
            const [d2KeyTotal, d2KeyLower, d2KeyUpper] = item[key].reduce(
              ([acc, lower, upper], obj) => {
                let estSum, lowerSum, upperSum;

                if (data2Key === "spend" && obj[data2Key] < 10) {
                  estSum = acc;
                } else {
                  estSum = acc + obj[data2Key];
                }
                // Spend doesn't have lower and upper bounds
                if (data2Key !== "spend") {
                  lowerSum = lower + obj[`${data2Key}Lower`];
                  upperSum = upper + obj[`${data2Key}Upper`];
                } else {
                  lowerSum = estSum;
                  upperSum = estSum;
                }

                return [estSum, lowerSum, upperSum];
              },
              [0, 0, 0]
            );

            let calcVal, calcValLower, calcValUpper;
            // calculating ROAS or CPA
            if (d2KeyTotal === 0) {
              calcVal = null;
              calcValLower = null;
              calcValUpper = null;
            } else {
              calcVal = dKeyTotal / d2KeyTotal;

              if (data1Key !== "spend") {
                // ROAS calculation: outcome/spend
                calcValLower = dKeyLower / d2KeyTotal;
                calcValUpper = dKeyUpper / d2KeyTotal;
              } else {
                // CPA calculation: spend/outcome
                calcValLower = d2KeyUpper === 0 ? null : dKeyTotal / d2KeyUpper;
                calcValUpper = d2KeyLower === 0 ? null : dKeyTotal / d2KeyLower;
              }
            }

            nameData[channels[index]].push({
              date: key,
              [channels[index]]: calcVal === Infinity || isNaN(calcVal) ? null : calcVal,
              [`${channels[index]}_$lower`]:
                calcValLower === Infinity || isNaN(calcValLower) ? null : calcValLower,
              [`${channels[index]}_$upper`]:
                calcValUpper === Infinity || isNaN(calcValUpper) ? null : calcValUpper,
              [`${channels[index]}_$band`]: isNaN(calcValUpper - calcValLower)
                ? null
                : calcValUpper - calcValLower,
            });
          } else {
            nameData[channels[index]].push({
              date: key,
              [channels[index]]: dKeyTotal,
              [`${channels[index]}_$lower`]: dKeyLower,
              [`${channels[index]}_$upper`]: dKeyUpper,
              [`${channels[index]}_$band`]: isNaN(dKeyUpper - dKeyLower)
                ? null
                : dKeyUpper - dKeyLower,
            });
          }
        });
      });

      const arr = Object.keys(nameData).map(key => {
        return nameData[key];
      });
      // flatten array of arrays and group by date. Gives {date: [{date, [channel, platform]: value}]}
      const groupByDat = R.groupBy(item => R.prop("date", item), arr.flat());

      grouping = Object.fromEntries(
        Object.keys(transformedChanPlat).map(key => {
          return [key.replace(" ", ""), transformedChanPlat[key].grouping];
        })
      );
      totalsByDate = getTotalsByDate(dateGrouping, groupByDat, fiscalCal);

      newD = getNewD({ totalsByDate, relative });

      break;
    }
  }

  const keys = Object.keys(newD[0]).filter(
    key =>
      key !== "date" &&
      !key.includes("_$lower") &&
      !key.includes("_$upper") &&
      !key.includes("_$band")
  );

  let areas: {
    name: string;
    dataKey: string;
    grouping: { channel: string; platform: string; tactic: string; brand: string };
  }[] = [];
  keys.forEach((key, index) => {
    key = groupBy === "platform" ? key.replace(" ", "") : key;

    areas[index] = {
      dataKey: key,
      grouping: grouping ? grouping[key] : null,
      name: capitalizeWords(key),
    };
  });

  let groupedAreas = [] as any[];
  if (Array.isArray(areas) && areas.length > 0 && areas[0].grouping) {
    groupedAreas = areas.reduce((acc: { channel: string; areas: any[] }[], obj) => {
      const { channel } = obj.grouping || { channel: "unknown" }; // Default to 'unknown' if channel is not present
      const existingGroup = acc.find(item => item.channel === channel);

      if (existingGroup) {
        existingGroup.areas.push(obj);
      } else {
        acc.push({ channel, areas: [obj] });
      }

      return acc;
    }, []);
  }

  areas = R.sortBy(R.compose(R.toLower, R.prop("name")))(areas);
  groupedAreas = R.sortBy(R.compose(R.toLower, R.prop("channel")))(groupedAreas);

  return { newD, areas, groupedAreas };
};

export const percentageToSplitFraction = (percentageStr: string): string => {
  const holdout = Math.round(parseFloat(percentageStr.replace("%", "")));
  const nonHoldout = 100 - holdout;
  return `${nonHoldout}/${holdout}`;
};

export const formatSnapshotData = (data: any[]): any[] => {
  return data.map((item: any) => {
    return {
      name: capitalizeWords(item.channel).replace(/,/g, " –"),
      value: item["Next Best Dollar Effectiveness"],
    };
  });
};

/*
 * MODEL INSIGHTS
 */

export const saturationDataFormatter = (
  data: ResponseCurves[],
  channelPlatformCounts: Record<string, Set<string>>
): { newD; meanValues; groupedLines } => {
  const d = R.groupBy(item => R.prop("name", item), data);

  const meanValues = {};
  for (const [key, value] of Object.entries(d)) {
    const meanRow = value.find(row => row.spendMultiplier === 1);
    meanValues[key] = meanRow
      ? { mean: meanRow.spend, dataPoint: meanRow.spend }
      : { mean: null, dataPoint: null };
  }

  const transformedData: {
    name: string;
    data: any[];
    grouping: { channel: string; platform: string; tactic: string; brand_nonbrand: string };
  }[] = R.pipe(
    R.toPairs,
    R.map(([name, data]: [string, any[]]) => {
      const channel = capitalizeWords(data[0].channel);
      let platform = capitalizeWords(data[0].platform);
      const tactic = capitalizeWords(data[0].tactic);
      const brand_nonbrand = capitalizeWords(data[0].brand_nonbrand);
      if (!platform && !brand_nonbrand && !tactic) {
        // Check if this channel has multiple platforms using channelPlatformCounts
        const channelName = data[0].channel;
        const platformCount = channelPlatformCounts[channelName]?.size || 0;
        platform = platformCount > 1 ? `All ${channel}` : "";
      }

      return {
        name: capitalizeWords(name),
        data: data as any[],
        grouping: { channel, platform, tactic, brand_nonbrand },
      };
    })
  )(d);

  let groupedLines = [] as any[];
  if (Array.isArray(transformedData) && transformedData.length > 0 && transformedData[0].grouping) {
    groupedLines = transformedData.reduce((acc: { channel: string; areas: any[] }[], obj) => {
      const { channel } = obj.grouping || { channel: "unknown" }; // Default to 'unknown' if channel is not present
      const existingGroup = acc.find(item => item.channel === channel);

      if (existingGroup) {
        existingGroup.areas.push(obj);
      } else {
        acc.push({ channel, areas: [obj] });
      }

      return acc;
    }, []);
  }

  return { newD: transformedData, meanValues, groupedLines };
};

export const decayDataFormatter = (
  data: any[],
  yAxisDataKey: string,
  channelPlatformCounts: Record<string, Set<string>>
): { newD; lines; groupedLines } => {
  const d = R.groupBy(item => R.prop("period", item), data);
  let newD = [] as any[];
  let grouping = [] as any[];

  Object.keys(d).forEach((key, index) => {
    newD[index] = {};
    newD[index].date = key;

    d[key].forEach((item: any) => {
      newD[index][item.name] = item[yAxisDataKey] * 100;
      grouping.push({});

      if (item.platform === null && item.tactic === null && item.brand_nonbrand === null) {
        // Check if this channel has multiple platforms using channelBreakoutMap
        const channelName = item.channel;
        const platformCount = channelPlatformCounts[channelName]?.size || 0;
        const platformLabel = platformCount > 1 ? `All ${capitalizeWords(item.channel)}` : "";

        grouping[index][item.name] = {
          channel: capitalizeWords(item.channel),
          platform: platformLabel,
          tactic: null,
          brand_nonbrand: null,
        };
      } else {
        grouping[index][item.name] = {
          channel: capitalizeWords(item.channel),
          platform: capitalizeWords(item.platform),
          tactic: capitalizeWords(item.tactic),
          brand_nonbrand: capitalizeWords(item.brand_nonbrand),
        };
      }
    });
  });

  const keys = Object.keys(newD[0]).filter(key => key !== "date");
  let lines: {
    dataKey: string;
    grouping?: { channel: string; platform: string; tactic: string; brand: string };
    name: string;
  }[] = [];
  keys.forEach((key, index) => {
    lines[index] = {
      name: capitalizeWords(key),
      dataKey: key,
      grouping: grouping ? grouping[0][key] : null,
    };
  });

  let groupedLines = [] as any[];
  if (Array.isArray(lines) && lines.length > 0 && lines[0].grouping) {
    groupedLines = lines.reduce((acc: { channel: string; areas: any[] }[], obj) => {
      const { channel } = obj.grouping || { channel: "unknown" }; // Default to 'unknown' if channel is not present
      const existingGroup = acc.find(item => item.channel === channel);

      if (existingGroup) {
        existingGroup.areas.push(obj);
      } else {
        acc.push({ channel, areas: [obj] });
      }

      return acc;
    }, []);
  }

  return { newD, lines, groupedLines };
};

/*
 * GEOGRAPHIC ANALYSIS
 */

// Turns array of objects, where each object is a row in the table,
// into an array of arrays, where each inner array is a row in the table
// each inner array is an array of objects, where each object is a cell in the table
export const formatROITableData = ({
  data,
  tableHeaders,
  outcomeVariable,
}: {
  data: any[];
  tableHeaders: any[];
  outcomeVariable: "revenue" | "kpiVolume";
}): any[] => {
  let newD = [] as any[];
  let newDimension = [] as any[];

  data.forEach((item: any, i) => {
    newD[i] = [];
    newDimension[i] = [];
    tableHeaders.forEach(header => {
      let label =
        typeof item[header.id] === "number" || item[header.id] === null
          ? item[header.id]
          : capitalizeWords(item[header.id]);
      let formattedLabel;

      switch (header.id) {
        case "nextDollarEffectiveness":
          if (item[header.id] === null || item[header.id] === undefined) {
            formattedLabel = "--";
          } else if (item[header.id] === 0) {
            formattedLabel = "$0";
          } else if (item[header.id] < 0.01) {
            formattedLabel = "<$0.01";
          } else if (item[header.id] >= 0.01) {
            formattedLabel = formatMoney(Math.abs(item[header.id]), 2);
          }
          break;
        case "predictedResponseToMedianSpendLow":
        case "predictedResponseToMedianSpend":
        case "predictedResponseToMedianSpendHigh":
        case "previousQuarterMedianSpend":
          if (item[header.id] === null || item[header.id] === undefined) {
            formattedLabel = "--";
          } else if (outcomeVariable === "revenue" || header.id === "previousQuarterMedianSpend") {
            if (item[header.id] === 0) {
              formattedLabel = "$0";
            } else if (item[header.id] < 0.5) {
              formattedLabel = "<$1";
            } else if (item[header.id] < 1) {
              formattedLabel = "$1";
            } else if (item[header.id] > 1) {
              formattedLabel = formatMoney(item[header.id], 0);
            }
          } else if (item[header.id] === 0) {
            formattedLabel = "0";
          } else if (item[header.id] < 0.5) {
            formattedLabel = "<1";
          } else if (item[header.id] < 1) {
            formattedLabel = "1";
          } else if (item[header.id] > 1) {
            formattedLabel = formatNumber(item[header.id], 0);
          }
          break;
        case "name":
          formattedLabel = capitalizeWords(item[header.id]).replace(/,/g, " –");
          break;
        default:
          if (item[header.id] === null || item[header.id] === undefined) {
            formattedLabel = "--";
          } else if (item[header.id] === 0) {
            formattedLabel = outcomeVariable === "revenue" ? "0" : "$0";
          } else if (item[header.id] < 0.01) {
            formattedLabel = outcomeVariable === "revenue" ? "<0.01" : "<$0.01";
          } else if (item[header.id] >= 0.01) {
            const convDecimal = item[header.id] > 15 ? 0 : 2;
            formattedLabel =
              outcomeVariable === "revenue"
                ? formatNumber(item[header.id], convDecimal)
                : formatMoney(item[header.id], convDecimal);
          }
          break;
      }

      if (header.id === "name") {
        const contentLabel = formattedLabel || formattedLabel === "0" ? `${formattedLabel}` : "--";
        newDimension[i].push({
          [header.dimensionTypeName]: {
            value: label,
            label: contentLabel,
          },
        });
      }

      // Label is the value of the cell; value: header.label is the column name
      const contentLabel = formattedLabel || formattedLabel === 0 ? formattedLabel : "--";
      newD[i].push({
        value: label,
        label: contentLabel,
      });
    });
  });

  if (!R.isEmpty(newDimension[0])) {
    return newDimension.flat();
  }

  return newD;
};
